import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import EverTrue from 'app';
import DefaultPhoto from 'images/et_avatar_user_120.png';
import { Button } from '@evertrue/et-components';
import API from 'entities/helpers/api';
import _ from 'underscore';
import SessionSource from 'base/session/session-source';
import FlaggedComponent from 'components/flagged-component';
import { parseDataUrl } from 'entities/helpers/parse-data-url';

const PhotoSettings = ({ org_id, contact_id, user_photo, affiliation_id }) => {
  const [file_selected, setFileSelected] = useState(null);
  const [displayable_file, setDisplayableFile] = useState(user_photo || DefaultPhoto);
  const fileUploadInput = useRef(null);
  const acceptedFileTypes = ['image/jpeg', 'image/png', 'image/gif'];
  const maxFileSizeInBytes = 5 * 1024 * 1024;
  const invalidFileTypeMessage = 'Please select a valid image file (JPG, PNG, or GIF).';
  const fileTooLargeMessage = 'File size must be 5MB or less.';

  const handleSetFileUpload = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!acceptedFileTypes.includes(selectedFile.type)) {
      EverTrue.Alert.error(invalidFileTypeMessage);
      e.target.value = '';
      return;
    }

    if (selectedFile.size >= maxFileSizeInBytes) {
      EverTrue.Alert.error(fileTooLargeMessage);
      e.target.value = '';
      return;
    }

    setFileSelected(selectedFile);
    setDisplayableFile(URL.createObjectURL(selectedFile));
  };

  const handleCancel = () => {
    setFileSelected(null);
    if (file_selected) {
      setDisplayableFile(user_photo);
    } else {
      setDisplayableFile(DefaultPhoto);
    }
  };

  const getBase64 = (file, _) => {
    if (file === undefined || file === null) {
      return Promise.resolve();
    }

    return new Promise(function (resolve, reject) {
      const reader = new FileReader();
      reader.onload = function () {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  //naming convention for the file: {oid}{contact_id}{current_unix_timestamp}.{format}
  const handleSaveFileUpload = () => {
    const isRemoving = !file_selected;

    getBase64(file_selected).then(function (base64String) {
      let data;
      if (isRemoving) {
        data = { photo: null };
      } else {
        if (!base64String) {
          EverTrue.Alert.error('There was a problem reading your photo. Please try again.');
          return;
        }
        const { rawBase64, contentType } = parseDataUrl(base64String, file_selected.type);
        data = _.compactObject({ photo: rawBase64, photo_content_type: contentType });
      }

      API.AUTH.PHOTO.post({
        urlArgs: { affiliation_id },
        data: _.jsonStringify(data),
        disableAlerts: true,
      })
        .then((resp) => {
          const updatedPhotoUrl = isRemoving ? null : resp?.photo?.url;
          SessionSource.updatePhoto(updatedPhotoUrl);
          if (!isRemoving && updatedPhotoUrl) {
            setDisplayableFile(updatedPhotoUrl);
          }
          EverTrue.Alert.success(isRemoving ? 'Photo removed successfully' : 'Photo updated successfully');
        })
        .catch((error_) => {
          if (!isRemoving) {
            const apiMessage = error_?.responseJSON?.message || _.jsonParse(error_?.responseText, {})?.message;
            EverTrue.Alert.error(apiMessage || 'There was a problem uploading your photo. Please try again.');
          }
        });
    });
  };

  const disableUpload = user_photo === displayable_file;

  return (
    <div className="profile-photo-uploader--body">
      <div className="photo-overlay" onClick={handleCancel}>
        <img
          src={displayable_file}
          alt="user_photo"
          onError={(e) => {
            if (e.target.src !== DefaultPhoto) {
              e.target.src = DefaultPhoto;
            }
          }}
        />
        <div className="hover-remove">Remove</div>
      </div>
      <div className="flex-photo-controls">
        <label className="profile-photo-uploader--label" htmlFor="hidden-photo-selector" tab-index={0}>
          <input
            ref={fileUploadInput}
            id="hidden-photo-selector"
            className="upload-select"
            type="file"
            onChange={handleSetFileUpload}
            accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
          />
          <Button type="secondary" onClick={() => fileUploadInput.current.click()}>
            Select a Photo
          </Button>
        </label>
        <div className="upload-button">
          <Button disabled={disableUpload} className="upload-button" onClick={handleSaveFileUpload}>
            Update Photo
          </Button>
        </div>
      </div>
      <p className="profile-photo-uploader--note">Accepted file types: JPG, PNG, GIF. Maximum file size: 5MB.</p>
      <FlaggedComponent feature="supportImplementationTest">
        <div className="upload-button">
          <Button
            type="secondary"
            className="flagged-test-button"
            onClick={() => window.alert('You have clicked the flagged element!')}
          >
            Flagged Feature
          </Button>
        </div>
      </FlaggedComponent>
    </div>
  );
};

PhotoSettings.propTypes = {
  org_id: PropTypes.number,
  contact_id: PropTypes.number,
  user_photo: PropTypes.string,
  affiliation_id: PropTypes.number,
};

export default PhotoSettings;
